/* Hero Slider
 *
 * One Swiper owns the image track. The headline/CTA panels are deliberately NOT
 * a second Swiper and not inside the slides — they are a stack toggled by
 * `is-active` from Swiper's slide events, with the cross-fade handled in CSS.
 * `loop: true` clones slides, so anything inside one is duplicated; keeping the
 * panels out of the track is what lets `{{ block.shopify_attributes }}` stay
 * unique in the DOM.
 *
 * Swiper 8.4.3 is loaded globally in layout/theme.liquid.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.hero-slider';

  /* The <script> tag lives inside the section body, so a page with N instances
     of this section loads N identical script elements and the browser executes
     this IIFE N times — same-src scripts are NOT de-duplicated. A closure-scoped
     registry would therefore be empty on the 2nd run and every slider would get
     a second Swiper bound to the same element. The guard has to live on the DOM
     to survive re-execution. */
  var STARTED_ATTR = 'data-hero-slider-initialised';
  var pendingLoadHook = false;

  function reducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function t(root, key, fallback) {
    return root.dataset[key] || fallback;
  }

  function swiperOf(root) {
    var container = root.querySelector('.hero-slider__swiper');
    return container ? container.swiper : null;
  }

  function buildEffectOptions(effect) {
    if (effect === 'slide') {
      return { effect: 'slide' };
    }
    return { effect: 'fade', fadeEffect: { crossFade: true } };
  }

  function setPanel(root, index) {
    var panels = root.querySelectorAll('.hero-slider__panel');
    for (var i = 0; i < panels.length; i++) {
      var isActive = i === index;
      panels[i].classList.toggle('is-active', isActive);
      if (isActive) {
        panels[i].removeAttribute('aria-hidden');
      } else {
        panels[i].setAttribute('aria-hidden', 'true');
      }
    }
  }

  /* Every slide is in the DOM at once, so without this a screen reader reads the
     alt text of all of them in a row. Driven off `swiper.slides` rather than a
     querySelectorAll because that collection includes the duplicates loop mode
     creates, and `activeIndex` is an index into it. The slides hold no focusable
     content, so aria-hidden is enough here and `visibility: hidden` — which
     would break the fade — is reserved for the panels. */
  function setSlideVisibility(swiper) {
    var slides = swiper.slides;
    if (!slides) return;
    for (var i = 0; i < slides.length; i++) {
      if (i === swiper.activeIndex) {
        slides[i].removeAttribute('aria-hidden');
      } else {
        slides[i].setAttribute('aria-hidden', 'true');
      }
    }
  }

  function announce(root, index, total) {
    var status = root.querySelector('.hero-slider__status');
    if (!status) return;
    // While auto-rotating, the APG carousel pattern requires the live region to
    // be off — otherwise a screen reader is interrupted on every tick, forever.
    if (status.getAttribute('aria-live') === 'off') return;

    var panel = root.querySelector('.hero-slider__panel[data-index="' + index + '"]');
    var heading = panel && panel.querySelector('.hero-slider__heading');
    var label = heading ? heading.textContent.trim() : '';
    var template = t(root, 'i18nSlideOf', 'Slide {{ index }} of {{ total }}');
    var text = template.replace('{{ index }}', index + 1).replace('{{ total }}', total);
    status.textContent = text + (label ? ': ' + label : '');
  }

  function setLiveRegion(root, autoplayRunning) {
    var status = root.querySelector('.hero-slider__status');
    if (status) status.setAttribute('aria-live', autoplayRunning ? 'off' : 'polite');
  }

  /* Without loop the first/last arrows are dead ends. This uses aria-disabled,
     NOT the `disabled` property: disabling the button that currently holds focus
     makes the browser drop focus to <body>, and since the keyboard handler is
     scoped to the nav, the carousel then becomes unreachable by keyboard
     entirely. aria-disabled keeps it focusable; the click handler no-ops at the
     edge. Conventions §7. */
  function setArrowState(root, swiper, loop) {
    if (loop) return;
    var prev = root.querySelector('.hero-slider__arrow--prev');
    var next = root.querySelector('.hero-slider__arrow--next');
    if (prev) prev.setAttribute('aria-disabled', swiper.isBeginning ? 'true' : 'false');
    if (next) next.setAttribute('aria-disabled', swiper.isEnd ? 'true' : 'false');
  }

  function hideNav(root) {
    var nav = root.querySelector('.hero-slider__nav');
    var footer = root.querySelector('.hero-slider__footer');
    // Degrade to a clean static hero rather than leaving live-looking controls
    // that silently do nothing.
    if (nav) nav.hidden = true;
    if (footer) footer.hidden = true;
  }

  function init(root) {
    if (root.hasAttribute(STARTED_ATTR)) return;

    var container = root.querySelector('.hero-slider__swiper');
    if (!container) return;

    // Belt and braces: a Swiper already bound to this element means a previous
    // run got here first.
    if (container.swiper) {
      root.setAttribute(STARTED_ATTR, '');
      return;
    }

    var count = parseInt(root.dataset.count, 10) || 0;
    if (count < 1) return;

    if (typeof window.Swiper !== 'function') {
      hideNav(root);
      if (!pendingLoadHook) {
        pendingLoadHook = true;
        window.addEventListener('load', function () {
          pendingLoadHook = false;
          if (typeof window.Swiper === 'function') initAll(document);
        });
      }
      return;
    }

    var loop = root.dataset.loop === 'true' && count > 1;
    var speed = parseFloat(root.dataset.speed) || 0.8;
    var delay = parseFloat(root.dataset.autoplayDelay) || 6;
    var pauseOnHover = root.dataset.pauseOnHover === 'true';
    // Never auto-rotate for someone who asked for reduced motion. CSS alone does
    // not stop a Swiper animation. Conventions §7.
    var autoplayOn = root.dataset.autoplay === 'true' && count > 1 && !reducedMotion();

    var options = {
      speed: reducedMotion() ? 0 : Math.round(speed * 1000),
      loop: loop,
      allowTouchMove: count > 1,
      slidesPerView: 1,
      watchSlidesProgress: true,
      autoplay: autoplayOn
        ? { delay: Math.round(delay * 1000), disableOnInteraction: false }
        : false
    };

    var effectOptions = buildEffectOptions(root.dataset.effect);
    for (var key in effectOptions) {
      if (Object.prototype.hasOwnProperty.call(effectOptions, key)) {
        options[key] = effectOptions[key];
      }
    }

    /* Deliberately NOT using Swiper's `navigation` module. Its update() sets
       `el.disabled = true` on <button> elements at the ends of a non-looping
       slider — including the button that currently has focus, which makes the
       browser drop focus to <body> and leaves the carousel unreachable by
       keyboard. Wiring clicks by hand keeps the buttons focusable and lets
       aria-disabled carry the state instead. */

    var paginationEl = root.querySelector('.hero-slider__pagination');
    if (paginationEl) {
      options.pagination = { el: paginationEl, clickable: true };
    }

    // realIndex is loop-aware; index is not. Always drive the panels from it.
    function sync() {
      setPanel(root, this.realIndex);
      setSlideVisibility(this);
      setArrowState(root, this, loop);
      announce(root, this.realIndex, count);
    }

    options.on = { init: sync, slideChange: sync };

    var swiper;
    try {
      swiper = new window.Swiper(container, options);
    } catch (err) {
      // Leaving STARTED_ATTR unset lets a later shopify:section:load retry.
      hideNav(root);
      return;
    }

    root.setAttribute(STARTED_ATTR, '');
    setLiveRegion(root, autoplayOn);

    var toggle = root.querySelector('.hero-slider__toggle');
    if (toggle) {
      if (!autoplayOn) {
        /* The toggle is rendered from the merchant's `autoplay` setting alone,
           but autoplay is additionally suppressed under reduced motion. Left
           wired, the button would START autoplay for exactly the users who asked
           for none — and with Swiper's defaults, because `options.autoplay` was
           replaced with `false`. Conventions §7. */
        toggle.hidden = true;
      } else {
        toggle.addEventListener('click', function () {
          var sw = swiperOf(root);
          if (!sw || !sw.autoplay) return;
          var playing = toggle.dataset.state === 'playing';
          var label = toggle.querySelector('.visually-hidden');
          if (playing) {
            sw.autoplay.stop();
            toggle.dataset.state = 'paused';
            if (label) label.textContent = t(root, 'i18nPlay', 'Play slideshow');
          } else {
            sw.autoplay.start();
            toggle.dataset.state = 'playing';
            if (label) label.textContent = t(root, 'i18nPause', 'Pause slideshow');
          }
          setLiveRegion(root, !playing);
        });
      }
    }

    /* Swiper's pauseOnMouseEnter binds to the swiper element only. Here that is
       the whole hero anyway, but binding to the section root also covers the
       controls that sit outside the track. */
    if (autoplayOn && pauseOnHover) {
      root.addEventListener('mouseenter', function () {
        var sw = swiperOf(root);
        if (sw && sw.autoplay && sw.autoplay.running) sw.autoplay.pause();
      });
      root.addEventListener('mouseleave', function () {
        var sw = swiperOf(root);
        /* Swiper 8.4.3 exposes only {pause, run, start, stop} — there is NO
           `resume()`. Calling it threw a TypeError on every mouseout and left
           autoplay permanently dead (pause() leaves running === true, so the
           old `running` guard never caught it). Under the default fade effect
           it never recovered at all, because EffectFade sets virtualTranslate
           and Swiper's transitionend fallback then never fires. */
        if (!sw || !sw.autoplay || !sw.autoplay.paused) return;
        sw.autoplay.paused = false;
        sw.autoplay.run();
      });
    }

    /* Handlers read the live instance rather than closing over it, so a
       destroy/re-init cycle in the theme editor cannot leave a stale closure
       throwing on a torn-down Swiper. Conventions §7. */
    var arrows = root.querySelectorAll('.hero-slider__arrow');
    for (var a = 0; a < arrows.length; a++) {
      arrows[a].addEventListener('click', function () {
        var sw = swiperOf(root);
        if (!sw) return;
        if (this.getAttribute('aria-disabled') === 'true') return;
        if (this.classList.contains('hero-slider__arrow--prev')) sw.slidePrev();
        else sw.slideNext();
      });
    }

    /* Arrow keys only while focus is inside the nav. Binding to the section root
       would hijack the keys from the CTA and from anything a page-level script
       puts over the hero. */
    var nav = root.querySelector('.hero-slider__nav');
    if (nav) {
      nav.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        var sw = swiperOf(root);
        if (!sw) return;
        event.preventDefault();
        if (event.key === 'ArrowLeft') sw.slidePrev();
        else sw.slideNext();
      });
    }
  }

  function destroy(root) {
    var container = root.querySelector('.hero-slider__swiper');
    if (container && container.swiper && typeof container.swiper.destroy === 'function') {
      container.swiper.destroy(true, true);
    }
    root.removeAttribute(STARTED_ATTR);
  }

  function initAll(scope) {
    if (!scope) return;
    // On shopify:section:load the target may BE the slider or contain it.
    if (scope.matches && scope.matches(SECTION_SELECTOR)) init(scope);
    var roots = scope.querySelectorAll ? scope.querySelectorAll(SECTION_SELECTOR) : [];
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme editor: sections are re-rendered on edit, so tear down and rebuild. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var scope = event.target;
    if (!scope) return;
    if (scope.matches && scope.matches(SECTION_SELECTOR)) destroy(scope);
    var roots = scope.querySelectorAll ? scope.querySelectorAll(SECTION_SELECTOR) : [];
    for (var i = 0; i < roots.length; i++) destroy(roots[i]);
  });

  /* Jumping to a block in the editor should show that slide — and STAY there.
     Without stopping autoplay the preview rotates away from the slide the
     merchant just clicked in to edit. Conventions §7. */
  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest(SECTION_SELECTOR);
    if (!root) return;
    var swiper = swiperOf(root);
    var index = parseInt(event.target.dataset.index, 10);
    if (!swiper || isNaN(index)) return;
    if (swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();
    if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(index);
    } else {
      swiper.slideTo(index);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var root = event.target.closest(SECTION_SELECTOR);
    if (!root || root.dataset.autoplay !== 'true' || reducedMotion()) return;
    var swiper = swiperOf(root);
    if (swiper && swiper.autoplay) swiper.autoplay.start();
  });
})();
